// The plan panel's substance: what it's for, who's in it, and what's been decided.
//
// Three sections, each under its own small-caps eyebrow, rendered in this one place and
// handed unchanged to both `DecisionsPanel` (desktop) and `DecisionsMobileStrip` (mobile) —
// one assembly of this content rather than two that would have to be kept in step.
//
// The objective is not a decision and does not sit in the decisions list: it is what the
// whole exercise is for, facilitator-maintained, corrected the same way — by saying so in
// the conversation. It comes first because it is the frame everything below it sits inside.
//
// The people are the plan's roster, not a decision either: everyone the facilitator has
// heard of, arrived or not, each row saying how far they've got — identified, invited, or
// joined (`PersonRow.tsx`) — with no control to add or remove anybody — the note under the
// eyebrow is the only place that says how, because there is nothing here for a button to do.
//
// Non-asset decisions sit in the same list as assets, undistinguished, because "they are
// often the currency that settles the asset fights" and a section header saying otherwise
// would be this interface deciding they are a lesser category. Read the header of
// `DecisionCard` for why there is no control on any of them, and `DecisionsNote` for what
// stands in its place.
//
// The artifact request lives here rather than on a surface of its own: the artifact IS
// the decisions list, rendered on paper, and it is available at any point regardless of the
// plan's state.

import { useState } from 'react';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { Internals } from '../internals.js';
import type { PlanState } from '../plan/usePlan.js';

export type DecisionsProps = {
  state: PlanState;
  internals: Internals;
  client: ApiClient;
  planId: string;
};

export function Decisions({
  state,
  internals,
  client,
  planId,
}: DecisionsProps): JSX.Element | null {
  const snapshot = state.snapshot;
  if (snapshot === null) {
    return null;
  }
  const { DecisionCard, DecisionsNote, PersonRow } = internals;

  const objectives = snapshot.objectives.filter((objective) => objective.text !== null);

  return (
    <div className="decisions">
      {objectives.length === 0 ? null : (
        <div className="decisions__objective">
          <h2 className="decisions__objective-title">What this plan is for</h2>
          {objectives.map((objective) => (
            <p key={objective.id} className="decisions__objective-text">
              {objective.text}
            </p>
          ))}
        </div>
      )}
      <div className="decisions__people">
        <h2 className="decisionsPanel__title">People</h2>
        {/* The one instruction this section still owes a reader: there is no button here
            to add somebody or fix a wrong address (`PersonRow.tsx`), so the only way to
            do either has to be said somewhere, and this is the one place that names the
            roster at all. */}
        <p className="note">
          To add someone, or fix a wrong address, say so in the conversation.
        </p>
        <ul className="people__list">
          {snapshot.people.map((person) => (
            <PersonRow key={person.id} person={person} />
          ))}
        </ul>
      </div>
      <div className="decisions__section">
        <h2 className="decisionsPanel__title">Decisions</h2>
        <DecisionsNote />
        {snapshot.decisions.length === 0 ? (
          // `DecisionsNote` above already says where the list comes from, so this states
          // only the plain fact that it's empty rather than repeating that.
          <p className="decisions__empty">There is nothing on the list yet.</p>
        ) : (
          <ol className="decisions__list">
            {snapshot.decisions.map((decision) => (
              <DecisionCard key={decision.id} decision={decision} />
            ))}
          </ol>
        )}
        {/* Nothing to copy yet — an empty artifact isn't a real offer. */}
        {snapshot.decisions.length === 0 ? null : (
          <ArtifactRequest client={client} planId={planId} />
        )}
      </div>
    </div>
  );
}

/**
 * "Requestable at any point, regardless of the plan's state."
 *
 * Fetched rather than linked, because the route is bearer-authenticated and an anchor
 * carries no header. A 503 — the renderer is not configured — is reported as the plain
 * fact that a copy cannot be made right now, and not as an error about the plan.
 */
function ArtifactRequest({
  client,
  planId,
}: {
  client: ApiClient;
  planId: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const request = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    try {
      const { blob, filename } = await client.downloadArtifact(planId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setProblem(
        error instanceof ApiFailure && error.code === 'artifact_unavailable'
          ? 'A copy can’t be made just now. Everything here is still here.'
          : 'That didn’t download. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="artifact">
      <button
        type="button"
        className="button"
        disabled={busy}
        onClick={() => void request()}
      >
        Download a copy
      </button>
      {problem === null ? null : (
        <p className="artifact__problem" role="status">
          {problem}
        </p>
      )}
    </div>
  );
}
